// eslint-disable-next-line import/no-cycle
import accountRequestService from '@client/services/accountRequest';
// eslint-disable-next-line import/no-cycle
import userService from '@client/services/user';
import {CUSTODIANS} from '@client/util/constants/custodian';
import {TEMPLATES} from '@client/constants/email';
import {Logger, Category} from '@simpleinvest/front-end-logger';
import {workflow, workflowParams} from '@client/constants/workflow';
import {catAccountHolderTypes} from '@client/util/constants/accounts';
import {getUserId} from '@simpleinvest/front-end-auth';
import {USER_TIMEZONE} from '@client/util/constants';
// eslint-disable-next-line import/no-cycle
import {convertNewBeneficiariesForReturningUser} from '@client/util/beneficiary';
import {displayErrorBanner} from '@client/events/notification-bus';
import {CITIZENSHIPS} from '@client/constants/citizenships';

const logger = new Logger({
  category: Category.Accounts,
  fileName: 'requests.js',
});

const stateObj = {
  current: [],
  currentRequestId: null,
  currentStep: 'confirmation',
  panel: false,
  citizenshipDropdown: CITIZENSHIPS,
  incomeIntervals: [],
  netWorthIntervals: [],
  selectedCatAccountHolderType: null,
  dividendSetting: null,
};

const gettersObj = {
  clUserId: (_state, _getters, rootState) => rootState.user.id || rootState.user.current.cl_user_id,
  riaId: (_state, _getters, rootState) => rootState.user.current.ria_id || rootState.riaId,
  overallStatus: (state) => (state.current && state.current.length > 0 ? state.current[0] : null),
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  isFaiAffiliationSet: (_state, getters) => getters.overallStatus && getters.overallStatus.per_aff_fai,
  selectedCatAccountHolderType: (state) => state.selectedCatAccountHolderType,
  isCatAccountHolderValid: (_state, getters, _rootState, rootGetters) => {
    if (rootGetters['account/showCatAccountHolderType']) {
      return !!getters.selectedCatAccountHolderType;
    }

    return true;
  },
  currentRequests: (state) => state.current || [],
  currentRequestId: (state) => state.currentRequestId,
  currentRequestIds: (state) => state.current.map((request) => Number.parseInt(request.request_id, 10)),
  dividendSetting: ({dividendSetting}) => dividendSetting,
  isCheckedTerm: (_state, _getters, rootState) => {
    if (rootState.modal.setAcknowledge) {
      return true;
    }

    return false;
  },
  requestIds: (state) => {
    if (state.current && state.current.length > 0) {
      // eslint-disable-next-line unicorn/no-array-reduce, unicorn/prefer-spread
      return state.current.reduce((requestIds, request) => requestIds.concat([request.request_id]), []);
    }

    return null;
  },
};

const actions = {
  async findById(context, requestId) {
    // eslint-disable-next-line unicorn/no-fn-reference-in-iterator, unicorn/no-array-callback-reference
    const accountRequest = await accountRequestService.find(requestId);

    return accountRequest;
  },
  getPending(context, {clUserId, custodianId}) {
    return accountRequestService.getPending({clUserId, custodianId});
  },
  async getActive({commit, rootState, dispatch, state}, custodian = null) {
    const custodianId = custodian || rootState.user.custodianId;
    const custodianCode = Object.values(CUSTODIANS).find((item) => item.id === Number(custodianId))?.code;
    const toRoot = {root: true};

    const activeResponse = await accountRequestService.getActiveAccountRequests({custodian: custodianCode});

    if (rootState.account.batchId === null && activeResponse && activeResponse.length > 0) {
      dispatch('account/batchId', activeResponse[0].batch_ID, toRoot);
    }

    const activeAccounts = state.currentRequestId
      ? activeResponse.filter((item) => item.request_id === Number(state.currentRequestId))
      : activeResponse;

    commit('SET_REQUEST_DATA', activeAccounts);

    const transformedBeneficiaries = convertNewBeneficiariesForReturningUser(activeAccounts);

    dispatch('designations/setAllBeneficiariesByAccountRequestId', transformedBeneficiaries, toRoot);
    dispatch('designations/setAccountsTrustedContact', activeAccounts, toRoot);

    if (activeAccounts.length > 0) {
      const accountItem = await dispatch('findById', activeAccounts[0].request_id);

      commit('SET_DIVIDEND_SETTINGS', accountItem.dividendSetting);
      const {publicCompanyHolder, politicalExposureDetail, exchangeEntity} = accountItem.disclosures;

      dispatch('user/employment/publiclyTradedCompany/set', publicCompanyHolder, toRoot);
      dispatch('user/employment/publicOfficialInformation/set', politicalExposureDetail, toRoot);
      dispatch('user/employment/setFaiDetails', exchangeEntity, toRoot);
      commit('SET_CURRENT_REQUEST_ID', accountItem.id);
    }

    return activeResponse;
  },
  add({commit, state}, payload) {
    if (state.current && state.current.length === 0) {
      commit('SET_REQUEST_DATA', [payload]);
    } else {
      commit('ADD_REQUEST', payload);
    }

    commit('SET_CURRENT_REQUEST_ID', payload.request_id);
  },
  set({commit}, payload) {
    commit('SET_CURRENT', payload);
  },

  async save({state}) {
    const promises = state.current.map((request) =>
      accountRequestService.updateAccountRequestCompletions({
        accountRequestId: request.request_id,
        payload: {
          exchangeEntity: request.per_aff_fai,
          publicCompanyHolder: request.per_aff_ptc,
          politicalExposure: request.per_aff_spf,
          diffMailing: request.diff_mail_add,
        },
      })
    );

    await Promise.all(promises);
  },

  // eslint-disable-next-line require-await
  async getAdvisorTerms({getters, rootGetters}, source = null) {
    const {riaId} = getters;
    const {'cognitoUser/userId': clUserId, 'user/id': userId} = rootGetters;
    const defaultSource = workflow.SEGMENT_WORKFLOW;

    return accountRequestService.getAdvisorTerms({
      riaId,
      clUserId: Number.parseInt(clUserId || userId, 10),
      ...workflowParams[source || defaultSource],
    });
  },

  // eslint-disable-next-line require-await
  async getAccountOpeningTerms({rootGetters}, account) {
    const {'cognitoUser/userId': clUserId} = rootGetters;

    return accountRequestService.getAccountOpeningTerms({
      clUserId: Number.parseInt(clUserId, 10),
      accountRequestId: account.request_id,
    });
  },

  async submitAccountOpeningRequest({rootGetters, getters}) {
    const {'cognitoUser/userId': clUserId, 'user/id': userId} = rootGetters;
    const {riaId, currentRequests} = getters;

    const accountOpeningRequests = currentRequests.map((account) => {
      const {code} = Object.values(CUSTODIANS).find((item) => Number(item.id) === Number(account.cust_id));

      return accountRequestService.submitAccountOpeningRequest({
        accountRequestId: account.request_id,
        clUserId: Number.parseInt(clUserId || userId, 10),
        custodian: code,
        riaId,
        userTimezone: USER_TIMEZONE,
      });
    });

    try {
      await Promise.all(accountOpeningRequests);
    } catch (error) {
      displayErrorBanner('Something went wrong submitting your accounts.');
      logger.logException({error});
    }
  },

  // eslint-disable-next-line require-await
  async saveAdvisorTerms({rootGetters, getters}, source = null) {
    const {riaId} = getters;
    const {'cognitoUser/userId': clUserId, 'user/id': userId} = rootGetters;
    const defaultSource = workflow.SEGMENT_WORKFLOW;

    return accountRequestService.saveAdvisorTermsToDocVault({
      riaId,
      clUserId: clUserId || userId,
      ...workflowParams[source || defaultSource],
    });
  },

  async sendOnboardingEmail({rootGetters}) {
    const {'cognitoUser/userId': clUserId, 'user/riaUserId': riaUserId} = rootGetters;
    const template = TEMPLATES.ONBOARDING_COMPLETE;

    await userService.sendEmailToAdvisor({clUserId, riaUserId, template});
  },

  setCurrentStep({commit}, payload) {
    commit('SET_CURRENT_STEP', payload);
  },
  // eslint-disable-next-line require-await
  async setPtcStatus({commit}, payload) {
    commit('SET_PTC_STATUS', payload);
  },
  // eslint-disable-next-line require-await
  async setSpfStatus({commit}, payload) {
    commit('SET_SPF_STATUS', payload);
  },
  // eslint-disable-next-line require-await
  async setFaiStatus({commit}, payload) {
    commit('SET_FAI_STATUS', payload);
  },
  // eslint-disable-next-line require-await
  async setSameMailing({commit, state}, payload) {
    if (state.current && state.current.length > 0) {
      commit('SET_SAME_MAILING', payload ? 0 : 1);
    }
  },

  async saveRequestStatus({state}) {
    const clUserId = await getUserId();

    return new Promise((resolve) => {
      const promises = [];

      state.current.forEach((request) => {
        promises.push(
          accountRequestService.setAccountRequestStatusToFinishReview({
            clUserId,
            accountRequestId: request.request_id,
            request: {
              accountStatus: 4,
            },
          })
        );
      });

      Promise.all(promises).then(() => {
        resolve();
      });
    });
  },
  // eslint-disable-next-line require-await
  async setEntity({commit}, payload) {
    commit('SET_ENTITY', payload);
  },
  async getIraDepositTerms({rootGetters}, account) {
    const {'cognitoUser/userId': clUserId} = rootGetters;
    const response = await accountRequestService.getIraDepositTerms({
      clUserId: Number.parseInt(clUserId, 10),
      accountRequestId: account.request_id,
    });

    return Array.isArray(response) && response.length > 0 ? response[0] : '';
  },
  // eslint-disable-next-line require-await
  async getElectronicDeliveryTerms() {
    return accountRequestService.getElectronicDeliveryTerms();
  },
  async updateAccountRequestType({commit}, payload) {
    await accountRequestService.updateAccountRequestType({
      accountRequestId: payload.request_id,
      accountType: payload.account_type_name_id,
    });
    commit('SET_TYPE', payload);
  },
  setPanel({commit}, payload) {
    commit('SET_PANEL', payload);
  },
  setCurrentRequestId({commit}, payload) {
    commit('SET_CURRENT_REQUEST_ID', payload);
  },
  async getIncomeIntervals({commit, rootGetters}) {
    const response = await accountRequestService.getIncomeIntervals(rootGetters['user/custodianName']);

    commit('SET_INCOME_INTERVALS', response);

    return response;
  },
  async getNetWorthIntervals({commit, rootGetters}) {
    const response = await accountRequestService.getNetWorthIntervals(rootGetters['user/custodianName']);

    commit('SET_NET_WORTH_INTERVALS', response);

    return response;
  },
  async saveFai({commit}, payload) {
    try {
      const objResponse = await accountRequestService.updateAccountRequest(payload);

      await commit('user/employment/SET_ENTITY_EXCHANGE', objResponse.disclosures.exchangeEntity, {root: true});

      return objResponse;
    } catch (error) {
      throw new Error(error);
    }
  },
  clearPiiFromBennyInfo({commit}) {
    commit('CLEAR_PII_FROM_BENNY_INFO');
  },
  setCatAccountHolderType({commit}, accountHolder) {
    commit('SET_CAT_ACCOUNT_HOLDER_TYPE', accountHolder);
  },
  findCatAccountHolderType({commit}, accountRequest) {
    const {exchangeEntity} = accountRequest.disclosures;

    if (exchangeEntity) {
      const accountHolder = catAccountHolderTypes.find((item) => item.enumValue === exchangeEntity.catAccountholderType);

      commit('SET_CAT_ACCOUNT_HOLDER_TYPE', accountHolder);
    }
  },
  setDividendSettings({commit}, value) {
    commit('SET_DIVIDEND_SETTINGS', value);
  },
  async saveDividendSettings({getters, rootGetters}) {
    const {currentRequests, dividendSetting: currentDividend} = getters;
    const {'cognitoUser/userId': clUserId, 'settings/onboardingSegment': segment} = rootGetters;
    const dividendSetting = currentDividend || segment?.dividendSetting;

    if (!dividendSetting) {
      return;
    }

    await Promise.all(
      currentRequests.map(({request_id: id, request_initiated_from: requestInitiator}) =>
        accountRequestService.updateAccountRequest({id, clUserId, dividendSetting, requestInitiator})
      )
    );
  },
  setCurrentAccount({commit, rootGetters}, accountRequestId) {
    const account = rootGetters['accountList/findAccountByRequestId'](accountRequestId);

    commit('SET_CURRENT_ACCOUNT', account);
  },
};

// mutations
const mutations = {
  SET_CURRENT_STEP(state, payload) {
    state.currentStep = payload;
  },
  SET_REQUEST_DATA(state, payload) {
    state.current = payload;
    state.current.forEach((request) => {
      // eslint-disable-next-line no-param-reassign
      request.account_type_name ??= '';
    });
  },
  ADD_REQUEST(state, payload) {
    state.current.push(payload);
  },
  SET_DIVIDEND_SETTINGS(state, value) {
    state.dividendSetting = value;
  },
  SET_ENTITY(state, payload) {
    state.current.forEach((request) => {
      if (request.faiDetails) {
        // eslint-disable-next-line no-param-reassign
        request.faiDetails.entity = payload;
      } else {
        // eslint-disable-next-line no-param-reassign
        request.faiDetails = {entity: payload};
      }
    });
  },
  SET_PTC_STATUS(state, payload) {
    state.current.forEach((request) => {
      // eslint-disable-next-line no-param-reassign
      request.per_aff_ptc = payload;
    });
  },
  SET_SPF_STATUS(state, payload) {
    state.current.forEach((request) => {
      // eslint-disable-next-line no-param-reassign
      request.per_aff_spf = payload;
    });
  },
  SET_FAI_STATUS(state, payload) {
    state.current.forEach((request) => {
      // eslint-disable-next-line no-param-reassign
      request.per_aff_fai = payload;
    });
  },
  SET_SAME_MAILING(state, payload) {
    state.current.forEach((request) => {
      // eslint-disable-next-line no-param-reassign
      request.diff_mail_add = payload;
    });
  },
  SET_CURRENT(state, payload) {
    state.current = payload;
  },
  SET_TYPE(state, payload) {
    if (!state.current[0]) {
      return;
    }

    const currentArr = state.current;

    currentArr[0] = {...currentArr[0], account_type: payload.account_type, account_type_name: payload.account_name};

    state.current = [...currentArr];
  },
  SET_PANEL(state, payload) {
    state.panel = payload;
  },
  SET_CURRENT_REQUEST_ID(state, payload) {
    state.currentRequestId = payload;
  },
  SET_INCOME_INTERVALS(state, payload) {
    state.incomeIntervals = payload;
  },
  SET_NET_WORTH_INTERVALS(state, payload) {
    state.netWorthIntervals = payload;
  },
  CLEAR_PII_FROM_BENNY_INFO(state) {
    state.current = state.current.map((request) => {
      const requestWithoutPii = {...request};

      requestWithoutPii.beneficiaries = request.beneficiaries.map((benny) => {
        const bennyWithoutPii = {...benny};

        bennyWithoutPii.benny_ssn = '';
        bennyWithoutPii.masked_benny_ssn = '';

        return bennyWithoutPii;
      });

      return requestWithoutPii;
    });
  },
  SET_CAT_ACCOUNT_HOLDER_TYPE(state, accountHolder) {
    state.selectedCatAccountHolderType = accountHolder;
  },
  SET_CURRENT_ACCOUNT(state, account) {
    state.current = [
      {
        request_id: account.requestId,
        account_type_name: account.type,
      },
    ];
  },
};

export default {
  namespaced: true,
  state: stateObj,
  getters: gettersObj,
  actions,
  mutations,
};
